import { Space } from "antd";
import type { AnalysisAgentTableType } from "./types/AnalysisAgentTableType";
import DropdownExamplesVariants from "../widgets/DropdownExamplesVariants";
import SubmitActions from "../widgets/SubmitActions";
import { StyledTable, StyledValue } from "../../analysisAgent.style";
import React, { useEffect, useMemo, useState } from "react";
import { useAnalysisAgentContext } from "../../_analysisAgentContext";
import type { AttrStatDataType } from "../../UseFetchType";
import SourceTag from "../../../../masterComponents/SourceTag";
import type { TableRowSelection } from "antd/es/table/interface";
import { usePayloadCheckboxesActionsContext } from "../../_payloadCheckboxesActionsContext";
import AgentTableToolbar from "../widgets/AgentTableToolbar";
import { getBatchLevelConfig } from "../../../../../utils/batchLevels";
import { ALL_BATCHES } from "../../../../../entities/entities";

const AnalysisAgentTable: React.FC<AnalysisAgentTableType> = ({
  attributeStatisticsData,
  page,
  pageSize,
  setPageSize,
  totalRows,
  loadMore,
  renderExpanded,
  fetchAttributeStatistics,
  disabled,
  isLoadingAttrStatChildSubmit,
  handleAttrStatChildSubmit,
  handleAttrStatChildValidation,
  isLoadingAttrStatChildValidation,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const {
    deferredActionsData,
    setDeferredActionsData,
    parentRowExamplesParams,
    setParentRowExamplesParams,
    tableSort,
    setTableSort,
    filtersForStatus,
    setFiltersForStatus,
    parentRowSourceValuesParams,
    setParentRowSourceValuesParams,
    selectedBatches,
  } = useAnalysisAgentContext();

  const parentBatchLevel = useMemo(
    () => getBatchLevelConfig(ALL_BATCHES, selectedBatches, 0),
    [selectedBatches],
  );

  const {
    selectedParentCheckboxKeys,
    setSelectedParentCheckboxKeys,
    setSelectedChildCheckboxKeys,
    dataRowForActions,
    setDataRowForActions,
    resetPayloadSelection,
    pendingBtn,
    setPendingBtn,
  } = usePayloadCheckboxesActionsContext();

  useEffect(() => {
    if (!isLoadingAttrStatChildSubmit) {
      setPendingBtn(null);
    }
  }, [isLoadingAttrStatChildSubmit]);

  useEffect(() => {
    if (!attributeStatisticsData?.length) return;
    const parentHashes = attributeStatisticsData.map((r) => r.hash);
    setDeferredActionsData((prev) =>
      parentHashes.length
        ? prev.filter((item) => parentHashes.includes(item.hash))
        : prev,
    );
  }, [expandedKeys]);

  useEffect(() => {
    return () => {
      setExpandedKeys([]);
      resetPayloadSelection();
      setParentRowExamplesParams({
        targetTable: null,
        hash: null,
      });
      setParentRowSourceValuesParams({
        targetTable: null,
        hash: null,
        state: null,
      });
      setDeferredActionsData([]);
    };
  }, [attributeStatisticsData]);

  useEffect(() => {
    if (expandedKeys[0] == null) return;
    setSelectedChildCheckboxKeys([]);
    setDataRowForActions((prev) =>
      prev.filter((row) => row.actionSource === "parent"),
    );
  }, [expandedKeys]);

  const parentHashesOnPage = useMemo(
    () => attributeStatisticsData?.map((r) => r.hash) ?? [],
    [attributeStatisticsData],
  );

  const columns = [
    {
      title: "State",
      dataIndex: "verifiedPercentage",
      key: "verifiedPercentage",
      // filters: [
      // 	{
      // 		text: "All Done",
      // 		value: "all done",
      // 	},
      // 	{
      // 		text: "Final Stages",
      // 		value: "final stages",
      // 	},
      // 	{
      // 		text: "Middle Stages",
      // 		value: "middle stages",
      // 	},
      // 	{
      // 		text: "Early Stages",
      // 		value: "early stages",
      // 	},
      // 	{
      // 		text: "Not Started",
      // 		value: "not started",
      // 	},
      // ],
      // filteredValue: filtersForStatus,
      render: (_: unknown, record: AttrStatDataType) => {
        return (
          <Space direction="vertical">
            {record.sources
              .slice()
              .sort((a, b) => a.source.localeCompare(b.source))
              .map((v) => (
                <SourceTag source={v.source} />
              ))}
          </Space>
        );
      },
    },
    {
      title: "Characteristic Value",
      dataIndex: "value",
      sorter: true,
      sortOrder: tableSort?.field === "value" ? tableSort.order : null,
      render: (value) => <StyledValue>{value}</StyledValue>,
    },
    {
      title: "Count",
      dataIndex: "frequency",
      sorter: true,
      sortOrder: tableSort?.field === "frequency" ? tableSort.order : null,
      render: (frequency: number, record: AttrStatDataType) => (
        <span
          style={{ height: 0 }}
          title={
            record.sources?.length
              ? record.sources
                  .map(
                    (obj) =>
                      `${obj.source === "<empty>" ? "N/A" : obj.source}: ${obj.count} (${obj.percentage})`,
                  )
                  .join("\n")
              : ""
          }
        >
          {/* {frequency[0]} ({frequency[1] > 1 ? frequency[1] : `<1`}%) */}
          {frequency}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (_: any, record: AttrStatDataType) => (
        <Space>
          <SubmitActions
            hash={record.hash}
            value={record.value}
            setDeferredActionsData={setDeferredActionsData}
            fetchAttributeStatistics={fetchAttributeStatistics}
            state={record.sources.map((s) => s.source)[0]}
          />

          <DropdownExamplesVariants
            getExamples={() => {
              setParentRowExamplesParams({
                hash: record.hash,
                targetTable: "parent",
              });
            }}
            reviewSourceValues={() => {
              setParentRowSourceValuesParams({
                hash: record.hash,
                targetTable: "parent",
                state: record.sources.map((s) => s.source)[0],
              });
            }}
            isFinalLevel={parentBatchLevel.isFinal}
            onSelectHash={() => {
              setExpandedKeys([record.hash]);
            }}
          />
        </Space>
      ),
    },
  ];

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys: selectedParentCheckboxKeys,
    onChange: (newKeys) => {
      setSelectedParentCheckboxKeys(newKeys);
      setDataRowForActions((prev) => {
        const dropChildRowsBecauseOwnerSelected =
          expandedKeys[0] != null && newKeys.some((k) => k === expandedKeys[0]);

        const keepOffParentPage = prev.filter((row) => {
          if (parentHashesOnPage.some((h) => h === row.hash)) return false;
          if (
            dropChildRowsBecauseOwnerSelected &&
            row.actionSource === "child"
          ) {
            return false;
          }
          return true;
        });
        const fromParent = (attributeStatisticsData ?? [])
          .filter((r) => newKeys.some((k) => k === r.hash))
          .map((r) => ({
            hash: r.hash,
            target_value: r.value,
            source_value: r.value,
            tag: (r.sources ?? []).flatMap((s) => s.source)[0] ?? "",
            actionSource: "parent",
            frequency: r.frequency,
          }));
        return [...keepOffParentPage, ...fromParent];
      });
    },
  };

  return (
    <StyledTable
      className="primary-table parent-table"
      rowKey="hash"
      rowSelection={rowSelection}
      showSorterTooltip={false}
      tableLayout="auto"
      rowClassName={(record: AttrStatDataType) => {
        const classes: string[] = [];
        if (
          record.hash === parentRowExamplesParams.hash &&
          parentRowExamplesParams.targetTable === "parent"
        ) {
          classes.push("highlight-row");
        }

        if (
          record.hash === parentRowSourceValuesParams.hash &&
          parentRowSourceValuesParams.targetTable === "parent"
        ) {
          classes.push("values-from-hash");
        }

        if (deferredActionsData.some((item) => item.hash === record.hash)) {
          classes.push("row-overlay");
        }

        if (record.hash === expandedKeys[0]) {
          classes.push("highlight-row-selected-1");
        }

        return classes.join(" ");
      }}
      columns={columns}
      dataSource={attributeStatisticsData}
      onChange={(pagination, filters, sorter, extra) => {
        if (pagination.pageSize !== pageSize) {
          setPageSize(pagination.pageSize);
          return;
        }
        switch (extra.action) {
          case "sort":
            setTableSort(sorter);
            break;
          case "filter":
            if (filters.verifiedPercentage != filtersForStatus)
              setFiltersForStatus(filters.verifiedPercentage);
            break;
          default:
            if (pagination.current !== page) {
              loadMore(pagination.current);
            }
            break;
        }
      }}
      pagination={{
        disabled: Boolean(
          deferredActionsData.length || selectedParentCheckboxKeys.length,
        ),
        current: page,
        pageSize: pageSize,
        total: totalRows,
        showSizeChanger: true,
        pageSizeOptions: ["15", "25", "50", "100"],
        showQuickJumper: true,
        showTotal: () => (
          <AgentTableToolbar
            mode="AgentTable"
            rows={dataRowForActions}
            onSubmit={handleAttrStatChildSubmit}
            onValidate={handleAttrStatChildValidation}
            isLoadingSubmit={isLoadingAttrStatChildSubmit}
            isLoadingValidate={isLoadingAttrStatChildValidation}
            disabled={disabled}
            pendingBtn={pendingBtn}
            setPendingBtn={setPendingBtn}
          />
        ),
      }}
      expandable={
        renderExpanded && {
          expandedRowKeys: expandedKeys,
          expandIcon: () => null,
          expandedRowClassName: (record: AttrStatDataType) =>
            record.hash === expandedKeys[0] ? "highlight-row-selected-1" : "",
          expandedRowRender: (record: AttrStatDataType) => {
            if (record.hash !== expandedKeys[0]) return null;
            return renderExpanded(record);
          },
        }
      }
    />
  );
};

export default AnalysisAgentTable;
